/**
 * JobReviews — visualização e submissão de avaliações.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Star } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Textarea } from "@/components/ui/textarea";
import {
  jobReviewsApi,
  type JobReview,
  type ReviewDirection,
} from "@/lib/api-job-extras";
import { formatRelative } from "@/lib/format";
import { cn } from "@/lib/utils";

const LABEL: Record<ReviewDirection, string> = {
  client_to_nerd: "Cliente avaliou o técnico",
  nerd_to_client: "Técnico avaliou o cliente",
};

export function JobReviews({ jobId, canSubmit = true }: { jobId: string; canSubmit?: boolean }) {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["job-reviews", jobId],
    queryFn: () => jobReviewsApi.list(jobId),
    enabled: !!jobId,
  });

  const submit = useMutation({
    mutationFn: (vars: { direction: ReviewDirection; score: number; comment?: string }) =>
      jobReviewsApi.submit(jobId, vars),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["job-reviews", jobId] });
      qc.invalidateQueries({ queryKey: ["job", jobId] });
      toast.success("Avaliação salva");
    },
    onError: (e: Error) => toast.error("Falha", { description: e.message }),
  });

  return (
    <div className="space-y-4">
      {canSubmit && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ReviewComposer
            title="Avaliar como cliente"
            description="Como foi o serviço do técnico?"
            onSubmit={(score, comment) =>
              submit.mutate({ direction: "client_to_nerd", score, comment })
            }
            submitting={submit.isPending}
          />
          <ReviewComposer
            title="Avaliar como técnico"
            description="Como foi a experiência com o cliente?"
            onSubmit={(score, comment) =>
              submit.mutate({ direction: "nerd_to_client", score, comment })
            }
            submitting={submit.isPending}
          />
        </div>
      )}

      {/* Avaliações existentes */}
      {list.isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : list.data && list.data.length > 0 ? (
        <div className="space-y-2">
          {list.data.map((r) => (
            <ReviewCard key={r.id} review={r} />
          ))}
        </div>
      ) : !canSubmit ? (
        <EmptyState icon={Star} title="Sem avaliações" />
      ) : null}
    </div>
  );
}

function ReviewComposer({
  title,
  description,
  onSubmit,
  submitting,
}: {
  title: string;
  description: string;
  onSubmit: (score: number, comment?: string) => void;
  submitting?: boolean;
}) {
  const [score, setScore] = useState(0);
  const [comment, setComment] = useState("");
  return (
    <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-card/40 p-5">
      <h4 className="font-semibold text-sm">{title}</h4>
      <p className="text-xs text-muted-foreground mb-3">{description}</p>
      <div className="flex gap-1 mb-3" aria-label="Nota">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => setScore(n)}
            className="transition-transform hover:scale-110"
          >
            <Star
              className={cn(
                "h-7 w-7 transition-colors",
                n <= score
                  ? "fill-amber-500 text-amber-500"
                  : "text-slate-300 dark:text-white/15",
              )}
            />
          </button>
        ))}
      </div>
      <Textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        rows={2}
        placeholder="Comentário (opcional)"
      />
      <Button
        size="sm"
        className="mt-3"
        onClick={() => onSubmit(score, comment || undefined)}
        disabled={!score || submitting}
      >
        Enviar avaliação
      </Button>
    </div>
  );
}

function ReviewCard({ review }: { review: JobReview }) {
  return (
    <div className="rounded-xl border border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-card/40 p-4">
      <div className="flex items-center justify-between gap-3">
        <Badge variant="muted">{LABEL[review.direction]}</Badge>
        <span className="text-xs text-muted-foreground">{formatRelative(review.submitted_at)}</span>
      </div>
      <div className="flex items-center gap-1 mt-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <Star
            key={n}
            className={cn(
              "h-4 w-4",
              n <= review.score ? "fill-amber-500 text-amber-500" : "text-slate-300 dark:text-white/15",
            )}
          />
        ))}
        <span className="ml-2 text-sm font-bold tabular-nums">{review.score.toFixed(1)}</span>
      </div>
      {review.comment && <p className="mt-2 text-sm whitespace-pre-wrap">{review.comment}</p>}
    </div>
  );
}
